import React from 'react';
import { ArrowRight, ChevronRight, BarChart3, Mail, Users, Star, FileText, PieChart } from 'lucide-react';
import { Link } from 'react-router-dom';
import HeroSection from '../components/sections/HeroSection';
import FeatureCard from '../components/ui/FeatureCard';
import CTASection from '../components/sections/CTASection';

const Home: React.FC = () => {
  const features = [
    {
      icon: <Users className="w-8 h-8 text-primary" />,
      title: "Lead Capture System",
      description: "Convert website visitors into qualified leads with customizable forms and intelligent tracking."
    },
    {
      icon: <Mail className="w-8 h-8 text-primary" />,
      title: "Email Marketing",
      description: "Nurture relationships with automated email sequences tailored to each prospect's needs."
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-primary" />,
      title: "Performance Analytics",
      description: "Track campaign performance and ROI with comprehensive monthly reporting."
    },
    {
      icon: <FileText className="w-8 h-8 text-primary" />,
      title: "Custom Landing Pages",
      description: "Create high-converting landing pages designed specifically for financial services."
    },
    {
      icon: <Star className="w-8 h-8 text-primary" />,
      title: "Review Management",
      description: "Generate and manage 5-star reviews on Google and Yelp to build credibility."
    },
    {
      icon: <PieChart className="w-8 h-8 text-primary" />,
      title: "Advanced Campaigns",
      description: "Deploy sophisticated marketing campaigns targeted to specific client segments."
    }
  ];

  return (
    <div className="animate-fade-in">
      <HeroSection />
      
      {/* Vimeo Video Presentation */}
      <section className="section bg-white">
        <div className="container-custom">
          <div style={{padding:'56.25% 0 0 0', position:'relative'}}>
            <iframe 
              src="https://player.vimeo.com/video/1090671432?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479" 
              frameBorder="0" 
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media" 
              style={{position:'absolute', top:0, left:0, width:'100%', height:'100%'}} 
              title="Unlocking Growth_ How AdvisorGrow Transforms Your Financial Advisory Business (1)"
              className="video-frame"
            ></iframe>
          </div>
          <script src="https://player.vimeo.com/api/player.js"></script>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="section bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-4">
              Everything You Need to Grow Your Practice
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-gray-600">
              Our all-in-one platform gives you the tools to attract, convert and retain high-value clients.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {features.map((feature, index) => (
              <FeatureCard 
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
              />
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/features" className="inline-flex items-center text-primary font-medium hover:text-primary-light transition-colors">
              Explore all features <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </div>
      </section>
      
      {/* How it Works Section */}
      <section className="section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-4">
              How It Works
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-gray-600">
              Our platform simplifies client acquisition and nurturing so you can focus on what you do best.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <span className="text-primary font-bold text-xl">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Capture Leads</h3>
              <p className="text-gray-600">Deploy optimized lead capture forms across your digital presence to convert visitors into prospects.</p>
            </div>
            
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <span className="text-primary font-bold text-xl">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Nurture Relationships</h3>
              <p className="text-gray-600">Engage prospects with personalized content that builds trust and demonstrates your expertise.</p>
            </div>
            
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <span className="text-primary font-bold text-xl">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Convert & Grow</h3>
              <p className="text-gray-600">Turn prospects into clients and leverage reviews to attract even more high-value leads.</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

export default Home;